import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Scanner } from "@yudiel/react-qr-scanner";
import { BsQrCodeScan } from "react-icons/bs";
import { HiOutlineCamera, HiOutlineExclamationCircle, HiOutlineRefresh, HiOutlineShieldExclamation } from "react-icons/hi";
import logo from "@/assets/logo.png";
import { capture } from "@/instrument";
import { useLoading } from "@/context/LoadingContext.jsx";

const CheckIn = () => {
  const navigate = useNavigate();
  const [cameraActive, setCameraActive] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const id = searchParams.get("id");
  const laboratoryToken = searchParams.get("laboratoryToken");
  const { isLoading } = useLoading();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (laboratoryToken) {
      const mockResult = [{ rawValue: `laboratoryToken=${laboratoryToken}` }];
      handleScan(mockResult);
    }
  }, [laboratoryToken]);

  const handleScan = async (detectedCodes) => {
    try {
      if (!detectedCodes || detectedCodes.length === 0 || isLoading) return;

      const firstCode = detectedCodes[0];
      const scannedText = firstCode.rawValue;

      const extractedToken = scannedText.match(/laboratoryToken=([^&]*)/);
      if (!extractedToken) {
        setError({
          type: "invalid_qr",
          title: "QR code invalide",
          message: "Ce QR code n'est pas reconnu. Assurez-vous de scanner le code affiché dans votre laboratoire.",
        });
        return;
      }

      const extractedLaboratoryToken = extractedToken[1];
      navigate(`/check-in-questions?id=${id}&token=${token}&laboratoryToken=${extractedLaboratoryToken}`);
    } catch (err) {
      capture(err);
      setError({
        type: "scan_error",
        title: "Erreur de lecture",
        message: "Une erreur est survenue lors de la lecture du QR code. Veuillez réessayer.",
      });
    }
  };

  const handleLaunchCamera = async () => {
    try {
      if (isLoading === true) return;
      setError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError({
          type: "not_supported",
          title: "Caméra non disponible",
          message: "Votre navigateur ne prend pas en charge l'accès à la caméra. Essayez avec un autre navigateur comme Chrome ou Safari.",
        });
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      const tracks = stream.getTracks();
      if (tracks && tracks.length > 0) {
        tracks[0].stop();
      }
      setCameraActive(true);
    } catch (err) {
      if (err && err.name === "NotReadableError") {
        setError({
          type: "camera_busy",
          title: "Caméra occupée",
          message: "La caméra est utilisée par une autre application. Fermez les autres applications qui l'utilisent et réessayez.",
        });
        return;
      }
      if (err && err.name === "NotAllowedError") {
        const isDismissed = err.message && err.message.toLowerCase().includes("dismissed");
        if (isDismissed) {
          setError({
            type: "permission_dismissed",
            title: "Autorisation requise",
            message: "Vous devez autoriser l'accès à la caméra pour scanner le QR code. Appuyez sur 'Réessayer' et acceptez la demande d'autorisation.",
          });
          return;
        }
        setError({
          type: "permission_denied",
          title: "Accès refusé",
          message: "L'accès à la caméra a été refusé. Autorisez l'accès dans les paramètres de votre navigateur pour continuer.",
        });
        return;
      }
      capture(err);
      setError({
        type: "unknown",
        title: "Erreur",
        message: "Une erreur inattendue s'est produite. Veuillez réessayer.",
      });
    }
  };

  const handleScannerError = (err) => {
    capture(err);
    if (err && err.name === "NotReadableError") {
      setError({
        type: "camera_busy",
        title: "Caméra occupée",
        message: "La caméra est utilisée par une autre application. Fermez les autres applications qui l'utilisent et réessayez.",
      });
      setCameraActive(false);
      return;
    }
    setError({
      type: "scan_error",
      title: "Erreur de lecture",
      message: "Une erreur est survenue lors de la lecture. Veuillez réessayer.",
    });
  };

  const handleRetry = () => {
    setError(null);
    if (cameraActive === true) {
      setCameraActive(false);
      setTimeout(() => handleLaunchCamera(), 100);
    } else {
      handleLaunchCamera();
    }
  };

  const handleReloadPage = () => {
    window.location.reload();
  };

  const getErrorIcon = () => {
    if (error?.type === "permission_denied" || error?.type === "permission_dismissed") return <HiOutlineShieldExclamation className="w-8 h-8 text-amber-500" />;
    return <HiOutlineExclamationCircle className="w-8 h-8 text-red-500" />;
  };

  const getErrorBgColor = () => {
    if (error?.type === "permission_denied" || error?.type === "permission_dismissed") return "bg-amber-50 border-amber-200";
    return "bg-red-50 border-red-200";
  };

  const getErrorTitleColor = () => {
    if (error?.type === "permission_denied" || error?.type === "permission_dismissed") return "text-amber-800";
    return "text-red-800";
  };

  const getErrorMessageColor = () => {
    if (error?.type === "permission_denied" || error?.type === "permission_dismissed") return "text-amber-700";
    return "text-red-700";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      <header className="pt-8 pb-4 px-6">
        <img src={logo} alt="Logo" className="h-10 mx-auto" />
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">Validez votre présence</h1>
            <p className="text-gray-600 text-base leading-relaxed">Scannez le QR code affiché dans votre laboratoire pour confirmer votre arrivée</p>
          </div>

          <div className="relative">
            {cameraActive === true && error === null ? (
              <div className="relative rounded-2xl overflow-hidden shadow-elevated bg-black">
                <div className="aspect-square">
                  <Scanner
                    onScan={handleScan}
                    onError={handleScannerError}
                    constraints={{ facingMode: "environment" }}
                    components={{
                      finder: false,
                    }}
                    scanDelay={500}
                    sound={false}
                    styles={{
                      container: { width: "100%", height: "100%" },
                      video: { width: "100%", height: "100%", objectFit: "cover" },
                    }}
                  />
                </div>

                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-72 h-72 relative">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <span className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    Caméra active
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-8">
                {error !== null ? (
                  <div className="text-center">
                    <div className={`rounded-xl ${getErrorBgColor()} border p-6 mb-6`}>
                      <div className="flex justify-center mb-4">{getErrorIcon()}</div>
                      <h3 className={`font-semibold text-lg mb-2 ${getErrorTitleColor()}`}>{error.title}</h3>
                      <p className={`text-sm leading-relaxed ${getErrorMessageColor()}`}>{error.message}</p>
                    </div>

                    <div className="space-y-3">
                      <button
                        onClick={handleRetry}
                        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 text-white font-medium px-6 py-3 rounded-xl transition-all duration-200 active:scale-[0.98]"
                      >
                        <HiOutlineRefresh className="w-5 h-5" />
                        Réessayer
                      </button>

                      {error.type === "permission_denied" && (
                        <p className="text-xs text-gray-500 mt-4">
                          Sur iPhone : Réglages → Safari → Caméra → Autoriser
                          <br />
                          Sur Android : Paramètres → Applications → Chrome → Autorisations → Caméra
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary-50 flex items-center justify-center">
                      <BsQrCodeScan className="w-10 h-10 text-primary" />
                    </div>

                    <p className="text-gray-600 mb-6 text-sm">Autorisez l'accès à la caméra pour scanner le QR code du laboratoire</p>

                    <button
                      onClick={handleLaunchCamera}
                      className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 text-white font-medium px-6 py-3 rounded-xl transition-all duration-200 active:scale-[0.98]"
                    >
                      <HiOutlineCamera className="w-5 h-5" />
                      Activer la caméra
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <button onClick={handleReloadPage} className="text-sm text-gray-500 hover:text-primary transition-colors inline-flex items-center gap-1">
              <HiOutlineRefresh className="w-4 h-4" />
              Recharger la page si la caméra ne fonctionne pas
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckIn;
